/**
 * This is the main entrypoint file for the application.
 *
 * When loaded in the client side, the application is rendered in the
 * #root element.
 *
 * When the bundle created from this file is imported in the server
 * side, the exported `renderApp` function can be used for server side
 * rendering.
 *
 * Note that this file is required for the build process.
 */

// React 16 depends on the collection types Map and Set, as well as requestAnimationFrame.
// https://reactjs.org/docs/javascript-environment-requirements.html
import 'core-js/features/map';
import 'core-js/features/set';
import './styles/marketplaceDefaults.css';

import createCache from '@emotion/cache';
import { loadableReady } from '@loadable/component';
import { sdkTypes, SharetribeSdk, TokenStores } from '@vansite/ts-sharetribe-flex-sdk';
import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';

import {
  FacebookTrackingEventHandler,
  GoogleTrackingEventHandler,
  LoggingAnalyticsHandler,
} from './analytics/handlers';
import { ClientApp, renderApp } from './app';
import config from './config';
import { authInfo, enforceAuthEpoch } from './ducks/auth.duck';
import { startUpVanSite } from './ducks/startUp.duck';
import { fetchCurrentUser } from './ducks/user.duck';
import routeConfiguration from './routeConfiguration';
import configureStore from './store';
import * as apiUtils from './util/api';
import localStorageCookieStore from './util/localStorageCookieStore';
import * as log from './util/log';
import { logError } from './util/log';
import { isCordova } from './util/mobile/deviceDetection';
import { initCordovaScripts } from './util/mobile/mobile';
import { matchPathname } from './util/routes';
import * as sample from './util/sample';

const cache = createCache({ key: 'css' });

/**
 * Load translation messages for the given locale.
 * Must match the same chunk names used in LocalizedRouter.js and app.js.
 */
async function loadMessagesForLocale(locale) {
  switch (locale) {
    case 'de':
      return (await import(/* webpackChunkName: "messages_de" */ './translations/de.json')).default;
    case 'en':
      return (await import(/* webpackChunkName: "messages_en" */ './translations/en.json')).default;
    case 'es':
      return (await import(/* webpackChunkName: "messages_es" */ './translations/es.json')).default;
    case 'fr':
      return (await import(/* webpackChunkName: "messages_fr" */ './translations/fr.json')).default;
    case 'it':
      return (await import(/* webpackChunkName: "messages_it" */ './translations/it.json')).default;
    case 'no':
      return (await import(/* webpackChunkName: "messages_no" */ './translations/no.json')).default;
    case 'sv':
      return (await import(/* webpackChunkName: "messages_sv" */ './translations/sv.json')).default;
    default:
      return (await import(/* webpackChunkName: "messages_en" */ './translations/en.json')).default;
  }
}

const render = async (store, shouldHydrate) => {
  try {
    await loadableReady();

    let ssrMessages = null;
    if (shouldHydrate && window.__SSR_LOCALE__) {
      ssrMessages = await loadMessagesForLocale(window.__SSR_LOCALE__);
    }

    const container = document.getElementById('root');

    if (shouldHydrate) {
      hydrateRoot(container, <ClientApp cache={cache} messages={ssrMessages} store={store} />);

      const authInfoLoaded = store.getState().auth.authInfoLoaded;
      if (!authInfoLoaded) {
        store.dispatch(authInfo()).then(() => {
          store.dispatch(fetchCurrentUser());
        });
      } else {
        store.dispatch(fetchCurrentUser());
      }
      // Fire-and-forget (nie render-blockierend): einmaliger Zwangs-Logout,
      // wenn der Server eine neuere Auth-Epoch meldet (Lazy Migration, ADR-002).
      store.dispatch(enforceAuthEpoch());
      store.dispatch(startUpVanSite());
    } else {
      if (isCordova()) {
        await initCordovaScripts(store.dispatch);
      }
      const root = createRoot(container);
      root.render(<ClientApp cache={cache} store={store} />);
      const authInfoLoaded = store.getState().auth.authInfoLoaded;
      if (!authInfoLoaded) {
        await store.dispatch(authInfo());
      }
      store.dispatch(fetchCurrentUser());
      // Fire-and-forget (nie render-blockierend): einmaliger Zwangs-Logout,
      // wenn der Server eine neuere Auth-Epoch meldet (Lazy Migration, ADR-002).
      store.dispatch(enforceAuthEpoch());
      await store.dispatch(startUpVanSite());
    }
  } catch (e) {
    logError(e, 'browser-side-render-failed');
  }
};

const setupAnalyticsHandlers = () => {
  let handlers = [];

  // Log analytics page views and events in dev mode
  if (config.dev) {
    handlers.push(new LoggingAnalyticsHandler());
  }

  handlers.push(new GoogleTrackingEventHandler());
  handlers.push(new FacebookTrackingEventHandler());

  return handlers;
};

// If we're in a browser already, render the client application.
if (typeof window !== 'undefined') {
  // set up logger with Sentry DSN client key and environment
  log.logSetup();

  const baseUrl = config.sdk.baseUrl ? { baseUrl: config.sdk.baseUrl } : {};

  const preloadedState = window.__PRELOADED_STATE__ || '{}';
  const initialState = JSON.parse(preloadedState, sdkTypes.reviver);
  const options = {
    transitVerbose: config.sdk.transitVerbose,
    clientId: config.sdk.clientId,
    secure: config.usingSSL,
    typeHandlers: apiUtils.typeHandlers,
    tokenStore: new TokenStores.BrowserStore({
      clientId: config.sdk.clientId,
      secure: config.usingSSL,
    }),
    ...baseUrl,
  };

  if (isCordova()) {
    // @ts-ignore
    options.tokenStore = localStorageCookieStore({ clientId: config.sdk.clientId });
  }
  const sdk = new SharetribeSdk(options);

  const analyticsHandlers = setupAnalyticsHandlers();

  const store = configureStore(initialState, sdk, analyticsHandlers);

  render(store, !!window.__PRELOADED_STATE__);

  if (config.dev) {
    // Expose stuff for the browser REPL
    window.app = {
      config,
      sdk,
      sdkTypes,
      store,
      sample,
      routeConfiguration: routeConfiguration(),
    };
  }
}

// Show warning if CSP is not enabled
const CSP = process.env.REACT_APP_CSP;
const cspEnabled = CSP === 'block' || CSP === 'report';

if (CSP === 'report' && process.env.REACT_APP_ENV === 'production') {
  console.warn(
    'Your production environment should use CSP with "block" mode. Read more from: https://www.sharetribe.com/docs/ftw-security/how-to-set-up-csp-for-ftw/'
  );
} else if (!cspEnabled) {
  console.warn(
    "CSP is currently not enabled! You should add an environment variable REACT_APP_CSP with the value 'report' or 'block'. Read more from: https://www.sharetribe.com/docs/ftw-security/how-to-set-up-csp-for-ftw/"
  );
}

// Export the function for server side rendering.
export default renderApp;

// exporting matchPathname and configureStore for server side rendering.
// matchPathname helps to figure out which route is called and if it has preloading needs
// configureStore is used for creating initial store state for Redux after preloading
export { config, configureStore, matchPathname, routeConfiguration };
